(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ProgressAdvanceSection {
    constructor(section) {
      this.section = section;
      this.sectionId = section.getAttribute('data-section-id');
      this.triggerThreshold = parseFloat(section.getAttribute('data-trigger-threshold')) || 0.8;
      this.contentRevealThreshold = parseFloat(section.getAttribute('data-content-threshold')) || 30;
      this.itemCount = parseInt(section.getAttribute('data-item-count'), 10) || 0;
      this.lastScrollTop = 0;
      this.ticking = false;
      this.listening = false;

      this.items = [];
      for (let i = 1; i <= this.itemCount; i++) {
        const item = section.querySelector('.custom-progress-advance-item-' + this.sectionId + '-' + i);
        if (!item) continue;
        this.items.push({
          item,
          header: section.querySelector('.custom-progress-advance-header-' + this.sectionId + '-' + i),
          content: section.querySelector('.custom-progress-advance-content-' + this.sectionId + '-' + i),
          fill: section.querySelector('.custom-progress-advance-fill-' + this.sectionId + '-' + i),
          badge: section.querySelector('.custom-progress-advance-badge-' + this.sectionId + '-' + i),
        });
      }

      // Scroll-to buttons inside this section.
      section.addEventListener('click', (event) => {
        const button = event.target.closest('[data-scroll-target]');
        if (!button) return;
        const targetClass = button.getAttribute('data-scroll-target');
        if (!targetClass) return;
        const target = document.querySelector('.' + CSS.escape(targetClass));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start',
        });
      });

      if (this.items.length === 0) return;

      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      if (reducedMotion.matches) {
        this.showAllComplete();
      } else {
        this.observe();
      }

      reducedMotion.addEventListener('change', () => {
        if (reducedMotion.matches) {
          this.stopListening();
          if (this.observer) this.observer.disconnect();
          this.showAllComplete();
        } else {
          this.observe();
        }
      });
    }

    observe() {
      // Only attach the scroll handler while the section is actually on screen.
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.startListening();
            this.requestUpdate();
          } else {
            this.stopListening();
          }
        });
      });
      this.observer.observe(this.section);
      this.requestUpdate();
    }

    startListening() {
      if (this.listening) return;
      this.listening = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }

    stopListening() {
      if (!this.listening) return;
      this.listening = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    onScroll() {
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(this.update);
    }

    setBadge(badge, active) {
      if (!badge) return;
      const prefix = active ? 'active' : 'inactive';
      badge.style.setProperty('--cpa-badge-text-color', badge.dataset[prefix + 'Text']);
      badge.style.setProperty('--cpa-badge-bg-color', badge.dataset[prefix + 'Bg']);
      badge.style.setProperty('--cpa-badge-border-color', badge.dataset[prefix + 'Border']);
    }

    showAllComplete() {
      this.items.forEach(({ content, fill, badge }) => {
        if (fill) fill.style.height = '100%';
        if (content) content.classList.add('visible');
        this.setBadge(badge, true);
      });
    }

    update() {
      // All layout reads happen inside a single animation frame.
      this.ticking = false;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const windowHeight = window.innerHeight;
      const bottomThreshold = windowHeight * this.triggerThreshold;

      this.items.forEach(({ item, header, content, fill, badge }) => {
        if (!item || !header || !content || !fill) return;

        const itemRect = item.getBoundingClientRect();
        const headerRect = header.getBoundingClientRect();

        if (itemRect.top < windowHeight && itemRect.bottom > 0) {
          const headerPosition = headerRect.top;
          if (headerPosition <= bottomThreshold) {
            const pixelsPastTrigger = bottomThreshold - headerPosition;
            const fillDistance = windowHeight * 0.7;
            const fillHeight = Math.min(100, (pixelsPastTrigger / fillDistance) * 100);
            fill.style.height = fillHeight + '%';

            if (badge) {
              const badgeHeight = badge.getBoundingClientRect().height;
              const fillPixels = (fillHeight / 100) * item.offsetHeight;
              this.setBadge(badge, fillPixels > badgeHeight);
            }

            if (fillHeight > this.contentRevealThreshold) {
              content.classList.add('visible');
              fill.style.height = '100%';
            } else {
              content.classList.remove('visible');
            }
          } else {
            fill.style.height = '0%';
            content.classList.remove('visible');
            this.setBadge(badge, false);
          }
        } else if (itemRect.bottom <= 0) {
          fill.style.height = '100%';
          this.setBadge(badge, true);
          if (scrollTop > this.lastScrollTop) {
            content.classList.add('visible');
          } else {
            content.classList.remove('visible');
          }
        } else {
          fill.style.height = '0%';
          content.classList.remove('visible');
          this.setBadge(badge, false);
        }
      });

      this.lastScrollTop = scrollTop;
    }
  }

  const init = () => {
    document
      .querySelectorAll('.custom-progress-advance-section')
      .forEach((section) => new ProgressAdvanceSection(section));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
